/**
 * The languages this product runs in.
 *
 * In `lib` rather than beside the voice catalogue, which is the only screen reading them today:
 * every screen that offers a language has to offer the same one, and a copy per screen is how one
 * of them ends up offering a language nothing else knows about.
 */

export interface LanguageOption {
	/** BCP 47. The API accepts any well-formed tag; these are the ones we run today. */
	value: string;
	label: string;
}

export const LANGUAGES: LanguageOption[] = [
	{ value: "en", label: "English" },
	{ value: "ms-MY", label: "Bahasa Melayu" },
	{ value: "th", label: "Thai" },
	{ value: "id", label: "Bahasa Indonesia" },
	{ value: "zh-Hans-SG", label: "Mandarin (Simplified)" },
	{ value: "ta-SG", label: "Tamil" },
];

const LANGUAGES_BY_TAG = new Map(LANGUAGES.map((language) => [language.value, language]));

/** The tag itself where we have no name for it — a curated voice may use any well-formed tag. */
export function languageLabel(tag: string | null | undefined): string {
	if (!tag) return "—";
	return LANGUAGES_BY_TAG.get(tag)?.label ?? tag;
}

/**
 * Whether a curated voice answers a language an agent asked for.
 *
 * A mirror of `matchesLanguage` in `src/modules/campaign-requests/services/voices.service.ts`,
 * and it has to stay one: exact tags match, and so does a shared primary subtag, so `en` covers
 * `en-SG` and an `ms-MY` agent is offered the `ms` voices. Curating one row per regional variant
 * would mean maintaining the same five voices a dozen times over.
 *
 * Needed on the client because the catalogue is fetched whole in one call and grouped locally — a
 * screen offering four languages would otherwise make four round trips to render one list.
 *
 * Case-insensitive, because BCP 47 is: `zh-Hans-SG` and `zh-hans-sg` are the same tag.
 */
export function languageMatches(voiceLanguage: string, requested: string): boolean {
	const voice = voiceLanguage.toLowerCase();
	const want = requested.toLowerCase();

	return voice === want || voice.split("-")[0] === want.split("-")[0];
}
